import * as React from 'react';

import { Button, EntityDetails, Row } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  LucideCheckCircle,
  LucidePlay,
  LucideRotateCcw,
  LucideSave,
  LucideSquareCheck,
  LucideXCircle,
} from 'lucide-react';

import {
  approveCycleCountTaskAtom,
  approveLineAdjustmentAtom,
  cancelCycleCountTaskAtom,
  completeCycleCountTaskAtom,
  recordLineCountAtom,
  rejectLineAdjustmentAtom,
  reopenCycleCountTaskAtom,
  requestLineRecountAtom,
  startCycleCountTaskAtom,
} from '../../_graphql/mutationCycleCountTask';
import * as state from '../../_state/countTaskDetail.state';
import * as facilitiesState from '../../_state/facilities.state';
import { useTranslations } from '../../i18n/useTranslations';

import { CountTaskStatusBadge } from './CountTaskStatusBadge';
import { CycleCountTaskLinesContent } from './CycleCountTaskLinesContent';

import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

type CountTaskDetailTab = 'lines';

async function processInChunks<T>(
  items: T[],
  chunkSize: number,
  fn: (item: T) => Promise<unknown>,
): Promise<PromiseSettledResult<unknown>[]> {
  const results: PromiseSettledResult<unknown>[] = [];
  for (let i = 0; i < items.length; i += chunkSize) {
    const chunk = items.slice(i, i + chunkSize);
    const chunkResults = await Promise.allSettled(chunk.map(fn));
    results.push(...chunkResults);
  }
  return results;
}

const MUTATION_CHUNK_SIZE = 10;

export const CycleCountTaskDetailView = () => {
  const t = useTranslations();
  const toast = useToast();
  const [, setLocation] = useModuleLocation();

  const [currentTab, setCurrentTab] =
    React.useState<CountTaskDetailTab>('lines');
  const [localCounts, setLocalCounts] = React.useState<
    Record<string, number | null>
  >({});

  const {
    data: task,
    loadingState,
    error,
    refetch,
  } = useAtomValue(state.$remoteTaskDetail);

  const facilitiesById = useAtomValue(facilitiesState.$facilitiesById);
  const facility = React.useMemo(
    () => (task?.facilityId ? facilitiesById[task.facilityId] : null),
    [task?.facilityId, facilitiesById],
  );

  const startTask = useSetAtom(startCycleCountTaskAtom({}));
  const completeTask = useSetAtom(completeCycleCountTaskAtom({}));
  const approveTask = useSetAtom(approveCycleCountTaskAtom({}));
  const cancelTask = useSetAtom(cancelCycleCountTaskAtom({}));
  const reopenTask = useSetAtom(reopenCycleCountTaskAtom({}));
  const recordLineCount = useSetAtom(recordLineCountAtom({}));
  const approveLineAdjustment = useSetAtom(approveLineAdjustmentAtom({}));
  const rejectLineAdjustment = useSetAtom(rejectLineAdjustmentAtom({}));
  const requestLineRecount = useSetAtom(requestLineRecountAtom({}));

  const handleBack = React.useCallback(() => {
    setLocation('/inventory/counts/tasks');
  }, [setLocation]);

  const dispatchLineCounts = React.useCallback(
    async (
      entries: [string, number | null][],
      mutationFn: (input: {
        id: string;
        countedQuantity: number;
      }) => Promise<unknown>,
    ): Promise<{
      succeeded: number;
      failed: number;
      firstError: string | null;
    }> => {
      const results = await processInChunks(
        entries,
        MUTATION_CHUNK_SIZE,
        ([lineId, quantity]) =>
          mutationFn({ id: lineId, countedQuantity: quantity as number }),
      );
      const succeeded = results.filter((r) => r.status === 'fulfilled').length;
      const failed = results.filter((r) => r.status === 'rejected').length;
      let firstError: string | null = null;
      if (failed > 0) {
        const firstRejected = results.find(
          (r) => r.status === 'rejected',
        ) as PromiseRejectedResult;
        firstError =
          firstRejected.reason instanceof Error
            ? firstRejected.reason.message
            : t.cycleCountTasksView.detail.actions.unknownError;
      }
      if (failed === 0) {
        setLocalCounts({});
      } else if (succeeded > 0) {
        const succeededIds = new Set(
          entries
            .filter((_, i) => results[i].status === 'fulfilled')
            .map(([lineId]) => lineId),
        );
        setLocalCounts((prev) => {
          const next: Record<string, number | null> = {};
          for (const [key, value] of Object.entries(prev)) {
            if (!succeededIds.has(key)) {
              next[key] = value;
            }
          }
          return next;
        });
      }
      return { succeeded, failed, firstError };
    },
    [t],
  );

  const handleStart = React.useCallback(async () => {
    if (!task) return;
    try {
      await startTask({ id: task.id });
      toast.success(t.cycleCountTasksView.detail.actions.startSuccess);
      refetch();
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : t.cycleCountTasksView.detail.actions.unknownError;
      toast.error(t.cycleCountTasksView.detail.actions.startError(message));
    }
  }, [task, startTask, toast, t, refetch]);

  const handleSaveCounts = React.useCallback(async () => {
    if (!task) return;
    const entries = Object.entries(localCounts).filter(([, v]) => v != null);
    const { succeeded, failed, firstError } = await dispatchLineCounts(
      entries,
      recordLineCount,
    );
    if (failed === 0) {
      toast.success(t.cycleCountTasksView.detail.saveCountsSuccess);
    } else if (succeeded === 0) {
      toast.error(
        t.cycleCountTasksView.detail.saveCountsError(firstError ?? ''),
      );
    } else {
      toast.error(
        t.cycleCountTasksView.detail.saveCountsPartialError(succeeded, failed),
      );
    }
    refetch();
  }, [
    task,
    recordLineCount,
    localCounts,
    toast,
    t,
    refetch,
    dispatchLineCounts,
  ]);

  const handleComplete = React.useCallback(async () => {
    if (!task) return;
    const entries = Object.entries(localCounts).filter(([, v]) => v != null);
    if (entries.length > 0) {
      const { succeeded, failed, firstError } = await dispatchLineCounts(
        entries,
        recordLineCount,
      );
      if (failed > 0) {
        if (succeeded === 0) {
          toast.error(
            t.cycleCountTasksView.detail.saveCountsError(firstError ?? ''),
          );
        } else {
          toast.error(
            t.cycleCountTasksView.detail.saveCountsPartialError(
              succeeded,
              failed,
            ),
          );
        }
        refetch();
        return;
      }
    }
    try {
      await completeTask({ id: task.id });
      toast.success(t.cycleCountTasksView.detail.actions.completeSuccess);
      setLocalCounts({});
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : t.cycleCountTasksView.detail.actions.unknownError;
      toast.error(t.cycleCountTasksView.detail.actions.completeError(message));
    }
    refetch();
  }, [
    task,
    completeTask,
    recordLineCount,
    localCounts,
    toast,
    t,
    refetch,
    dispatchLineCounts,
  ]);

  const handleApprove = React.useCallback(async () => {
    if (!task) return;
    try {
      await approveTask({ id: task.id });
      toast.success(t.cycleCountTasksView.detail.actions.approveSuccess);
      refetch();
    } catch (err) {
      const message = err instanceof Error ? err.message : '';
      if (message.includes('lines requiring approval')) {
        toast.error(
          t.cycleCountTasksView.detail.actions.approveUnresolvedLinesError,
        );
      } else {
        toast.error(
          t.cycleCountTasksView.detail.actions.approveError(
            message || t.cycleCountTasksView.detail.actions.unknownError,
          ),
        );
      }
    }
  }, [task, approveTask, toast, t, refetch]);

  const handleCancel = React.useCallback(async () => {
    if (!task) return;
    try {
      await cancelTask({ id: task.id });
      toast.success(t.cycleCountTasksView.detail.actions.cancelSuccess);
      refetch();
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : t.cycleCountTasksView.detail.actions.unknownError;
      toast.error(t.cycleCountTasksView.detail.actions.cancelError(message));
    }
  }, [task, cancelTask, toast, t, refetch]);

  const handleReopen = React.useCallback(async () => {
    if (!task) return;
    try {
      await reopenTask({ id: task.id });
      toast.success(t.cycleCountTasksView.detail.actions.reopenSuccess);
      refetch();
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : t.cycleCountTasksView.detail.actions.unknownError;
      toast.error(t.cycleCountTasksView.detail.actions.reopenError(message));
    }
  }, [task, reopenTask, toast, t, refetch]);

  const handleApproveAdjustment = React.useCallback(
    async (lineId: string, unitCostOverride?: number) => {
      try {
        await approveLineAdjustment({ id: lineId, unitCostOverride });
        toast.success(t.cycleCountTasksView.detail.lineActions.approveSuccess);
        refetch();
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : t.cycleCountTasksView.detail.actions.unknownError;
        toast.error(
          t.cycleCountTasksView.detail.lineActions.approveError(message),
        );
      }
    },
    [approveLineAdjustment, toast, t, refetch],
  );

  const handleRejectAdjustment = React.useCallback(
    async (lineId: string) => {
      try {
        await rejectLineAdjustment({ id: lineId });
        toast.success(t.cycleCountTasksView.detail.lineActions.rejectSuccess);
        refetch();
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : t.cycleCountTasksView.detail.actions.unknownError;
        toast.error(
          t.cycleCountTasksView.detail.lineActions.rejectError(message),
        );
      }
    },
    [rejectLineAdjustment, toast, t, refetch],
  );

  const handleRequestRecount = React.useCallback(
    async (lineId: string) => {
      try {
        await requestLineRecount({ id: lineId });
        toast.success(t.cycleCountTasksView.detail.lineActions.recountSuccess);
        refetch();
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : t.cycleCountTasksView.detail.actions.unknownError;
        toast.error(
          t.cycleCountTasksView.detail.lineActions.recountError(message),
        );
      }
    },
    [requestLineRecount, toast, t, refetch],
  );

  const handleCountedQuantityChange = React.useCallback(
    (lineId: string, value: number | null) => {
      setLocalCounts((prev) => ({ ...prev, [lineId]: value }));
    },
    [],
  );

  const isEditable = task?.status === 'InProgress';
  const showLineActions =
    task?.status === 'Completed' || task?.status === 'Approved';

  const LinesContent = React.useMemo(() => {
    if (!task) return () => null;
    return () => (
      <CycleCountTaskLinesContent
        task={task}
        isEditable={isEditable}
        showLineActions={showLineActions}
        localCounts={localCounts}
        onCountedQuantityChange={handleCountedQuantityChange}
        onApproveAdjustment={handleApproveAdjustment}
        onRejectAdjustment={handleRejectAdjustment}
        onRequestRecount={handleRequestRecount}
      />
    );
  }, [
    task,
    isEditable,
    showLineActions,
    localCounts,
    handleCountedQuantityChange,
    handleApproveAdjustment,
    handleRejectAdjustment,
    handleRequestRecount,
  ]);

  return (
    <EntityDetails
      className="@CycleCountTaskDetailView"
      error={error}
      loading={loadingState.isInitialLoading}
      onBackPressed={handleBack}
      primaryTitle={
        task
          ? t.cycleCountTasksView.detail.title(task.taskNumber)
          : 'Loading...'
      }
      badges={task ? <CountTaskStatusBadge status={task.status} /> : null}
      subtitle={
        task ? (
          <div className="flex flex-wrap items-center gap-x-1">
            <span className="font-medium">{facility?.name}</span>
            {task.countType && (
              <>
                <span className="text-neutral/30">&middot;</span>
                <span>
                  {t.cycleCountTasksView.countTypes[task.countType] ??
                    task.countType}
                </span>
              </>
            )}
            {task.assignedToUserName && (
              <>
                <span className="text-neutral/30">&middot;</span>
                <span>{task.assignedToUserName}</span>
              </>
            )}
          </div>
        ) : undefined
      }
      headerActions={
        task ? (
          <Row gap2>
            {task.status === 'Pending' && (
              <Button
                icon={<LucidePlay />}
                color="primary"
                onPress={handleStart}
              >
                {t.cycleCountTasksView.detail.actions.start}
              </Button>
            )}
            {task.status === 'InProgress' && (
              <>
                <Button
                  icon={<LucideSave />}
                  variant="outline"
                  color="primary"
                  onPress={handleSaveCounts}
                  isDisabled={
                    Object.values(localCounts).filter((v) => v != null)
                      .length === 0
                  }
                >
                  {t.cycleCountTasksView.detail.saveCounts}
                </Button>
                <Button
                  icon={<LucideSquareCheck />}
                  color="primary"
                  onPress={handleComplete}
                >
                  {t.cycleCountTasksView.detail.actions.complete}
                </Button>
              </>
            )}
            {task.status === 'Completed' && (
              <Button
                icon={<LucideCheckCircle />}
                color="success"
                onPress={handleApprove}
              >
                {t.cycleCountTasksView.detail.actions.approve}
              </Button>
            )}
            {task.status !== 'Approved' && task.status !== 'Cancelled' && (
              <Button
                icon={<LucideXCircle />}
                variant="outline"
                color="failure"
                onPress={handleCancel}
              >
                {t.cycleCountTasksView.detail.actions.cancel}
              </Button>
            )}
            {task.status === 'Cancelled' && (
              <Button
                icon={<LucideRotateCcw />}
                variant="outline"
                color="primary"
                onPress={handleReopen}
              >
                {t.cycleCountTasksView.detail.actions.reopen}
              </Button>
            )}
          </Row>
        ) : null
      }
      tabs={{
        lines: {
          title: t.cycleCountTasksView.detail.linesTab,
          content: LinesContent,
        },
      }}
      currentTab={currentTab}
      onChangeTab={(tab) => setCurrentTab(tab as CountTaskDetailTab)}
    />
  );
};
